import { useState, useRef, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import Layout from "../components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import { ScrollArea } from "../components/ui/scroll-area";
import { 
  Sparkles, Send, ArrowLeft, Brain, Banknote, Shield, Heart, User, Loader2
} from "lucide-react";
import axios from "axios";
import { API } from "../App";
import { toast } from "sonner";

const agentConfig = {
  recruitment: {
    name: "Agente de Reclutamiento",
    description: "Especializado en selección de personal, análisis de CVs y evaluación de candidatos.",
    icon: Brain,
    color: "text-violet-600",
    bgColor: "bg-violet-100",
    suggestions: [
      "Analiza este CV y dame un resumen de fortalezas y debilidades",
      "Genera una descripción de puesto para Desarrollador Full Stack",
      "¿Qué preguntas debo hacer en una entrevista para un puesto de Marketing?",
      "Evalúa si este perfil es adecuado para una vacante de gerente"
    ]
  },
  payroll: {
    name: "Agente de Nómina",
    description: "Especializado en cálculos de nómina, cumplimiento fiscal y legislación laboral mexicana.",
    icon: Banknote,
    color: "text-blue-600",
    bgColor: "bg-blue-100",
    suggestions: [
      "¿Cómo se calcula el ISR para un salario de $30,000 mensuales?",
      "¿Cuáles son las deducciones obligatorias del IMSS?",
      "Explica cómo calcular el aguinaldo según la ley mexicana",
      "¿Qué es el INFONAVIT y cómo afecta la nómina?"
    ]
  },
  compliance: {
    name: "Agente de Compliance",
    description: "Especializado en cumplimiento legal, leyes laborales mexicanas y políticas de RH.",
    icon: Shield,
    color: "text-emerald-600",
    bgColor: "bg-emerald-100",
    suggestions: [
      "¿Cuáles son los días de vacaciones según la Ley Federal del Trabajo?",
      "¿Qué dice la ley sobre el teletrabajo en México?",
      "¿Cuáles son las obligaciones del patrón en materia de seguridad?",
      "¿Cómo se debe manejar un despido justificado?"
    ]
  },
  engagement: {
    name: "Agente de Engagement",
    description: "Especializado en clima laboral, retención de talento y bienestar organizacional.",
    icon: Heart,
    color: "text-rose-600",
    bgColor: "bg-rose-100",
    suggestions: [
      "¿Cómo puedo mejorar el engagement de mis empleados?",
      "Dame estrategias para reducir la rotación de personal",
      "¿Qué iniciativas de bienestar puedo implementar?",
      "¿Cómo medir la satisfacción laboral efectivamente?"
    ]
  }
};

const AIAgent = () => {
  const { agentType } = useParams();
  const agent = agentConfig[agentType];
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const messagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  if (!agent) {
    return (
      <Layout title="Agente no encontrado">
        <div className="text-center py-12">
          <p className="text-slate-600 mb-4">El agente solicitado no existe.</p>
          <Link to="/dashboard">
            <Button>Volver al Dashboard</Button>
          </Link>
        </div>
      </Layout>
    );
  }

  const handleSend = async () => {
    if (!input.trim() || loading) return;

    const userMessage = input.trim();
    setInput("");
    setMessages(prev => [...prev, { role: "user", content: userMessage }]);
    setLoading(true);

    try {
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      const response = await axios.post(`${API}/ai/agent`, {
        agent_type: agentType,
        prompt: userMessage
      }, { headers, withCredentials: true });

      setMessages(prev => [...prev, { role: "assistant", content: response.data.response }]);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || "Error al procesar la consulta";
      toast.error(errorMsg);
      setMessages(prev => [...prev, { 
        role: "assistant", 
        content: `Lo siento, ocurrió un error: ${errorMsg}. Por favor verifica que tienes una API key configurada en Configuración de IA.`
      }]);
    } finally {
      setLoading(false);
    }
  };

  const handleSuggestion = (suggestion) => {
    setInput(suggestion);
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Layout title={agent.name}>
      <Link to="/dashboard" className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6">
        <ArrowLeft className="w-4 h-4" />
        Volver al Dashboard
      </Link>

      <div className="max-w-4xl mx-auto">
        <Card className="border-slate-200 h-[calc(100vh-280px)] flex flex-col">
          {/* Header */}
          <CardHeader className="border-b border-slate-200 flex-shrink-0">
            <div className="flex items-center gap-4">
              <div className={`w-12 h-12 rounded-xl ${agent.bgColor} flex items-center justify-center`}>
                <agent.icon className={`w-6 h-6 ${agent.color}`} />
              </div>
              <div>
                <CardTitle className="text-lg">{agent.name}</CardTitle>
                <p className="text-sm text-slate-500">{agent.description}</p>
              </div>
            </div>
          </CardHeader>

          {/* Messages */}
          <CardContent className="flex-1 overflow-hidden p-0">
            <ScrollArea className="h-full p-4">
              {messages.length === 0 ? (
                <div className="h-full flex flex-col items-center justify-center text-center p-6">
                  <div className={`w-16 h-16 rounded-2xl ${agent.bgColor} flex items-center justify-center mb-4`}>
                    <Sparkles className={`w-8 h-8 ${agent.color}`} />
                  </div>
                  <h3 className="text-lg font-semibold text-slate-900 mb-2">
                    ¿En qué puedo ayudarte?
                  </h3>
                  <p className="text-slate-500 mb-6 max-w-md">
                    Soy tu asistente de IA especializado. Haz cualquier pregunta relacionada con mi área de expertise.
                  </p>
                  <div className="grid sm:grid-cols-2 gap-2 w-full max-w-lg">
                    {agent.suggestions.map((suggestion, index) => (
                      <Button
                        key={index}
                        variant="outline"
                        className="h-auto py-3 px-4 text-left text-sm whitespace-normal justify-start"
                        onClick={() => handleSuggestion(suggestion)}
                        data-testid={`suggestion-${index}`}
                      >
                        {suggestion}
                      </Button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="space-y-4">
                  {messages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex gap-3 ${message.role === "user" ? "justify-end" : ""}`}
                    >
                      {message.role === "assistant" && (
                        <div className={`w-8 h-8 rounded-lg ${agent.bgColor} flex items-center justify-center flex-shrink-0`}>
                          <Sparkles className={`w-4 h-4 ${agent.color}`} />
                        </div>
                      )}
                      <div
                        className={`max-w-[80%] rounded-xl p-4 ${
                          message.role === "user"
                            ? "bg-emerald-600 text-white"
                            : "bg-slate-100 text-slate-900"
                        }`}
                      >
                        <p className="whitespace-pre-wrap text-sm">{message.content}</p>
                      </div>
                      {message.role === "user" && (
                        <div className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                          <User className="w-4 h-4 text-emerald-600" />
                        </div>
                      )}
                    </div>
                  ))}
                  {loading && (
                    <div className="flex gap-3">
                      <div className={`w-8 h-8 rounded-lg ${agent.bgColor} flex items-center justify-center`}>
                        <Sparkles className={`w-4 h-4 ${agent.color}`} />
                      </div>
                      <div className="bg-slate-100 rounded-xl p-4">
                        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
                      </div>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </ScrollArea>
          </CardContent>

          {/* Input */}
          <div className="border-t border-slate-200 p-4 flex-shrink-0">
            <div className="flex gap-2">
              <Textarea
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Escribe tu consulta..."
                className="min-h-[44px] max-h-32 resize-none"
                rows={1}
                data-testid="ai-chat-input"
              />
              <Button
                onClick={handleSend}
                disabled={!input.trim() || loading}
                className={`${agent.bgColor.replace('bg-', 'bg-').replace('-100', '-600')} hover:opacity-90`}
                style={{ backgroundColor: agent.color.includes('violet') ? '#7c3aed' : 
                         agent.color.includes('blue') ? '#2563eb' : 
                         agent.color.includes('emerald') ? '#059669' : '#e11d48' }}
                data-testid="ai-send-btn"
              >
                <Send className="w-4 h-4" />
              </Button>
            </div>
            <p className="text-xs text-slate-400 mt-2">
              Presiona Enter para enviar, Shift+Enter para nueva línea
            </p>
          </div>
        </Card>
      </div>
    </Layout>
  );
};

export default AIAgent;
